import { useContextSelectors } from "@-/com.npmjs.use-context-selectors";
import type { Tagged } from "@-/com.npmjs.type-fest";
import { isNonEmpty } from "@-/com.npmjs.ts-array-length";
import { createGate } from "@-/dev.improve.haniverse.lib.gate";
import { MorphemeInfoPanelContext } from "@-/dev.improve.haniverse.routes.nav.app._components.morpheme.panels._components.panel-content._context";
import { useMorphemeCorpusFilter } from "@-/dev.improve.haniverse.lib.morpheme.corpus.use";
import { useMemo } from "react";

export type MorphemeInfoPanelDictionarySimilarSectionGate = Tagged<
  { graphemeList: readonly [string, ...string[]] },
  "MorphemeInfoPanelDictionarySimilarSectionGate"
>;

/**
 * Whether the Overview shows its "Similar" row, and which graphemes it lists. `null` is the
 * designed hidden state: a word, or a grapheme with no similar grapheme inside the user's browsing
 * corpus (`corpus_settings` — PRC tier 1 / Taiwan MOE 常用字 by default).
 */
export function useMorphemeInfoPanelDictionarySimilarSectionGate(): MorphemeInfoPanelDictionarySimilarSectionGate | null {
  const [infoBackCardSlice] = useContextSelectors(
    MorphemeInfoPanelContext,
    (c) => c.infoBackCardSlice.data,
  );
  const { isGraphemeInCorpus } = useMorphemeCorpusFilter();
  const corpusSimilarGraphemeList = useMemo(
    () =>
      infoBackCardSlice.morphemeType === "grapheme"
        ? infoBackCardSlice.similarGraphemeList.filter((grapheme) => isGraphemeInCorpus(grapheme))
        : [],
    [infoBackCardSlice, isGraphemeInCorpus],
  );
  return isNonEmpty(corpusSimilarGraphemeList)
    ? createGate<MorphemeInfoPanelDictionarySimilarSectionGate>({
        graphemeList: corpusSimilarGraphemeList,
      })
    : null;
}
